import {
  registerWidgetDataProvider,
  type WidgetLiveValues,
  type WidgetSourceRef,
} from "@docit/doc-widgets";
import type { ApiClient } from "~/api/client";

/**
 * Live values for a widget whose payload declares a `sourceRef`.
 *
 * The rule the registry enforces and this must not soften: **the stored payload is the
 * record.** A live value is merged onto a COPY of the series, behind a banner saying so, and
 * the document is never mutated. A figure in a document is what somebody measured; a live
 * quote is what is true this second, and conflating the two would make every old document
 * quietly wrong.
 *
 * Exactly one kind is registered — `pulse.quotes` — because exactly one exists.
 *
 * Two upstream defects are not reproduced (§F6.7):
 *  - it read `q.price ?? q.last`, and the server has never emitted `last`: dead fallback;
 *  - it discarded the server's per-quote `asOf` and stamped the BROWSER clock instead, which
 *    dates a value by when it was rendered rather than when it was taken.
 */
export function registerQuoteProvider(api: Pick<ApiClient, "quotes">): void {
  registerWidgetDataProvider("pulse.quotes", async (ref: WidgetSourceRef) => {
    const symbols = symbolsFrom(ref.params);
    if (symbols.length === 0) {
      return { values: {}, asOf: "" } satisfies WidgetLiveValues;
    }
    const snapshot = await api.quotes.get(symbols);
    // `available:false` is DATA — no provider is configured — not a failure. An empty result
    // simply means the refresh button found nothing to show.
    const values: Record<string, number | null> = {};
    let asOf = "";
    for (const quote of snapshot.quotes) {
      values[quote.symbol] = quote.price;
      // The SERVER's timestamp, and the newest one across the batch.
      if (quote.asOf && quote.asOf > asOf) {
        asOf = quote.asOf;
      }
    }
    return { values, asOf } satisfies WidgetLiveValues;
  });
}

/** `sourceRef.params` is untyped by design; read the symbols out of it defensively. */
function symbolsFrom(params: unknown): string[] {
  const record = params && typeof params === "object" ? (params as Record<string, unknown>) : {};
  const raw = record.symbols;
  if (Array.isArray(raw)) {
    return raw.filter((value): value is string => typeof value === "string");
  }
  return typeof raw === "string" ? [raw] : [];
}
